import React, { useState } from 'react';
import { Layout, Menu, Dropdown, Avatar, Button, Select, Space, theme } from 'antd';
import {
  DashboardOutlined,
  ProjectOutlined,
  ToolOutlined,
  CalculatorOutlined,
  PercentageOutlined,
  ShopOutlined,
  UserOutlined,
  LogoutOutlined,
  GlobalOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth';

const { Header, Sider, Content } = Layout;
const { Option } = Select;

const MainLayout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);
  const { token: { colorBgContainer } } = theme.useToken();

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('menu.dashboard')
    },
    {
      key: '/projects',
      icon: <ProjectOutlined />,
      label: t('menu.projects')
    },
    {
      key: '/ie',
      icon: <ToolOutlined />,
      label: t('menu.ieData')
    },
    {
      key: '/cost',
      icon: <CalculatorOutlined />,
      label: t('menu.costCalc')
    },
    {
      key: '/rates',
      icon: <PercentageOutlined />,
      label: t('menu.rates')
    },
    {
      key: '/materials',
      icon: <ShopOutlined />,
      label: t('menu.materials')
    },
    ...(user?.role.code === 'admin' || user?.role.code === 'cost_admin' ? [{
      key: '/users',
      icon: <UserOutlined />,
      label: t('menu.users')
    }] : [])
  ];

  const handleMenuClick = (e: { key: string }) => {
    navigate(e.key);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="profile" icon={<UserOutlined />}>
        {user?.realName}
      </Menu.Item>
      <Menu.Item key="role" disabled>
        {i18n.language === 'zh' ? user?.role.name : user?.role.nameEn}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        {t('auth.logout')}
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        theme="light"
        style={{ borderRight: '1px solid #f0f0f0' }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          {collapsed ? (
            <span style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>AC</span>
          ) : (
            <span style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
              Amphenol AUTO
            </span>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header style={{
          padding: '0 24px',
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16 }}
          />
          
          <Space>
            <Select
              value={i18n.language}
              onChange={changeLanguage}
              style={{ width: 100 }}
              suffixIcon={<GlobalOutlined />}
              size="small"
            >
              <Option value="en">English</Option>
              <Option value="zh">中文</Option>
            </Select>
            
            <Dropdown overlay={userMenu} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar style={{ backgroundColor: '#1890ff' }} icon={<UserOutlined />} />
                <span>{user?.realName}</span>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{
          margin: 24,
          padding: 24,
          background: colorBgContainer,
          borderRadius: 8,
          minHeight: 'calc(100vh - 112px)'
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;